/**
 * Utility functions for building hierarchical target trees from flat target lists.
 * Target names use `/` as path separators (e.g., "stm32/app", "esp32/ota/updater").
 */

/**
 * Get the leaf (last segment) of a target name.
 * "esp32/ota/updater" -> "updater"
 * "nrf52" -> "nrf52"
 */
export const getLeafName = (targetName) => {
  const parts = targetName.split('/');
  return parts[parts.length - 1];
};

/**
 * Get breadcrumb segments (all segments except the last).
 * "esp32/ota/updater" -> ["esp32", "ota"]
 * "nrf52" -> []
 */
export const getBreadcrumbSegments = (targetName) => {
  const parts = targetName.split('/');
  return parts.slice(0, -1);
};

/**
 * Build a hierarchical tree from a flat list of targets.
 * Targets without `/` go into rootTargets. Targets with `/` are nested into folderNodes.
 *
 * @param {Array<{name: string, architecture?: string, toolchain?: string}>} targets
 * @returns {{ rootTargets: Array, folderNodes: Array<{name: string, fullPath: string, children: Array, targets: Array}> }}
 */
export const buildTargetTree = (targets) => {
  const rootTargets = [];
  const folderMap = new Map();

  targets.forEach(target => {
    const parts = target.name.split('/');

    if (parts.length === 1) {
      rootTargets.push(target);
      return;
    }

    // Walk down the path, creating folder nodes as needed
    let currentMap = folderMap;
    for (let i = 0; i < parts.length - 1; i++) {
      const segment = parts[i];
      const fullPath = parts.slice(0, i + 1).join('/');

      if (!currentMap.has(segment)) {
        currentMap.set(segment, {
          name: segment,
          fullPath,
          children: new Map(),
          targets: []
        });
      }

      const node = currentMap.get(segment);
      if (i === parts.length - 2) {
        // Last folder segment — add the target here
        node.targets.push(target);
      } else {
        currentMap = node.children;
      }
    }
  });

  const mapToArray = (map) => {
    return Array.from(map.values()).map(node => ({
      name: node.name,
      fullPath: node.fullPath,
      children: mapToArray(node.children),
      targets: node.targets
    }));
  };

  return {
    rootTargets,
    folderNodes: mapToArray(folderMap)
  };
};

/**
 * Count all leaf targets in a tree (root + nested in folders).
 */
export const countTargetsInNode = (node) => {
  let count = node.targets.length;
  node.children.forEach(child => {
    count += countTargetsInNode(child);
  });
  return count;
};

/**
 * Filter a target tree, keeping only branches that contain matching leaf targets.
 * Returns a pruned copy of the tree.
 *
 * @param {{ rootTargets: Array, folderNodes: Array }} tree
 * @param {string} filterTerm - case-insensitive search term
 * @returns {{ rootTargets: Array, folderNodes: Array, totalCount: number }}
 */
export const filterTargetTree = (tree, filterTerm) => {
  if (!filterTerm) {
    let total = tree.rootTargets.length;
    tree.folderNodes.forEach(node => {
      total += countTargetsInNode(node);
    });
    return { ...tree, totalCount: total };
  }

  const term = filterTerm.toLowerCase();

  const filterNode = (node) => {
    const matchingTargets = node.targets.filter(t =>
      t.name.toLowerCase().includes(term)
    );
    const filteredChildren = node.children
      .map(filterNode)
      .filter(child => child !== null);

    if (matchingTargets.length === 0 && filteredChildren.length === 0) {
      return null;
    }

    return {
      ...node,
      targets: matchingTargets,
      children: filteredChildren
    };
  };

  const filteredRootTargets = tree.rootTargets.filter(t =>
    t.name.toLowerCase().includes(term)
  );

  const filteredFolderNodes = tree.folderNodes
    .map(filterNode)
    .filter(node => node !== null);

  let totalCount = filteredRootTargets.length;
  filteredFolderNodes.forEach(node => {
    totalCount += countTargetsInNode(node);
  });

  return {
    rootTargets: filteredRootTargets,
    folderNodes: filteredFolderNodes,
    totalCount
  };
};

/**
 * Group targets by first `/` segment for use in <optgroup> rendering.
 * Targets without `/` go into `ungrouped`. Others grouped by first segment,
 * with display text showing the remainder after the first segment.
 *
 * @param {Array<{name: string}>} targets
 * @returns {{ ungrouped: Array<{name: string, displayName: string}>, groups: Object<string, Array<{name: string, displayName: string}>> }}
 */
export const groupTargetsForSelect = (targets) => {
  const ungrouped = [];
  const groups = {};

  targets.forEach(target => {
    const slashIndex = target.name.indexOf('/');
    if (slashIndex === -1) {
      ungrouped.push({ name: target.name, displayName: target.name });
    } else {
      const group = target.name.substring(0, slashIndex);
      const remainder = target.name.substring(slashIndex + 1);
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push({ name: target.name, displayName: remainder });
    }
  });

  return { ungrouped, groups };
};
